/**
 * File: firebase.config.ts
 * Path: packages/config/src/firebase/firebase.config.ts
 * Description: Client-side Firebase SDK client initialization with automatic environment emulator parsing.
 */

/// <reference types="vite/client" />

import { initializeApp, getApps, getApp, type FirebaseApp } from "firebase/app";
import { FUNCTIONS_EMULATOR_PORT, FUNCTIONS_REGION } from "./functionsUrl";
import { getAuth as _getAuth, type Auth } from "firebase/auth";
import {
  getFirestore,
  initializeFirestore,
  connectFirestoreEmulator,
  type Firestore,
} from "firebase/firestore";
import {
  getStorage as _getStorage,
  type FirebaseStorage,
} from "firebase/storage";
import {
  getFunctions as _getFunctions,
  connectFunctionsEmulator,
  type Functions,
} from "firebase/functions";

/* -------------------------------------------------------------------------
 * 1. SHARED CONFIGURATION
 * ------------------------------------------------------------------------- */
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const isBrowser = () => typeof window !== "undefined";
const emulatorsEnabled = () =>
  isBrowser() && import.meta.env.VITE_USE_EMULATORS === "true";

/* -------------------------------------------------------------------------
 * 2. CLIENT-SIDE INITIALIZATION
 * ------------------------------------------------------------------------- */
let _app: FirebaseApp | null = null;
let _db: Firestore | null = null;
let _auth: Auth | null = null;
let _storage: FirebaseStorage | null = null;
let _functions: Functions | null = null;
let _emulatorsBound = false;

function ensureClient(name: string): void {
  if (!isBrowser()) {
    throw new Error(`[firebase-client] ${name} accessed during SSR runtime.`);
  }
}

export function getFirebaseApp(): FirebaseApp {
  ensureClient("getFirebaseApp");
  if (_app) return _app;
  _app = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
  return _app;
}

export function getDb(): Firestore {
  ensureClient("getDb");
  if (_db) return _db;
  const app = getFirebaseApp();
  try {
    _db = initializeFirestore(app, { experimentalAutoDetectLongPolling: true });
  } catch {
    _db = getFirestore(app);
  }
  return _db;
}

export function getAuthInstance(): Auth {
  ensureClient("getAuthInstance");
  if (_auth) return _auth;
  _auth = _getAuth(getFirebaseApp());
  return _auth;
}

export function getStorageInstance(): FirebaseStorage {
  ensureClient("getStorageInstance");
  if (_storage) return _storage;
  _storage = _getStorage(getFirebaseApp());
  return _storage;
}

export function getFunctionsInstance(): Functions {
  ensureClient("getFunctionsInstance");
  if (_functions) return _functions;
  // Same constant the handlers and hosting rewrites use, so a region move
  // can never leave the callable client pointing at the old region.
  _functions = _getFunctions(getFirebaseApp(), FUNCTIONS_REGION);
  return _functions;
}

// Client Instance Proxies — guarded so a missing/invalid apiKey in preview
// sandboxes does not throw at module evaluation time. Consumers that actually
// need Firebase will surface the real error on first call.
const safe = <T,>(fn: () => T): T =>
  (isBrowser() && firebaseConfig.apiKey
    ? (() => {
        try {
          return fn();
        } catch (err) {
          if (typeof console !== "undefined") {
            console.warn("[firebase-client] deferred init failed:", err);
          }
          return undefined as unknown as T;
        }
      })()
    : (undefined as unknown as T));

// App Check re-exports so consumers importing "@idk/config/firebase" get them too.
export { initAppCheck, getAppCheckHeaders } from "./appCheckService";
export { resolveHttpFunctionUrl, buildHttpFunctionUrl } from "./functionsUrl";

export const db = safe(getDb);
export const auth = safe(getAuthInstance);
export const storage = safe(getStorageInstance);
export const functions = safe(getFunctionsInstance);


/* -------------------------------------------------------------------------
 * 3. EMULATOR BINDING
 * --------------------------------------------------------- */
/**
 * Ports come from firebase.json → emulators. Firestore was pointed at 5000,
 * which is the website's dev server, so a developer running with
 * VITE_USE_EMULATORS=true had every Firestore call go somewhere that speaks
 * HTML.
 */
const FIRESTORE_EMULATOR_PORT = 8080;

function bindEmulators(): void {
  if (_emulatorsBound || !emulatorsEnabled()) return;
  _emulatorsBound = true;
  try {
    connectFirestoreEmulator(getDb(), "127.0.0.1", FIRESTORE_EMULATOR_PORT);
    connectFunctionsEmulator(
      getFunctionsInstance(),
      "127.0.0.1",
      FUNCTIONS_EMULATOR_PORT,
    );
  } catch {
    // Graceful exception intercept for hot reload executions
  }
}

if (isBrowser()) {
  queueMicrotask(bindEmulators);
}
